/**
 * Config for the feedback form + donate button.
 *
 * Both values are PUBLIC-SAFE to ship in client code:
 *   - The Web3Forms access key only permits POSTing to the form
 *     relay keyed to your inbox; it can't read anything.
 *   - The Buy Me a Coffee URL is a public page.
 *
 * Two ways to set each value:
 *   1. Vite env var at build time (VITE_WEB3FORMS_KEY / VITE_BMAC_URL).
 *      For the Docker/Coolify build, add them as build-time env vars
 *      in Coolify so vite inlines them. Preferred — keeps the values
 *      out of the committed source.
 *   2. Replace the placeholder string below directly. Simplest, but
 *      the value lands in git history.
 *
 * Get a free Web3Forms key at https://web3forms.com (enter your
 * email, no signup — the key is emailed to you). Submissions arrive
 * as email at that address, ready to be scanned / actioned by hand
 * (or by a future automated pass).
 */

const WEB3FORMS_PLACEHOLDER = 'YOUR_WEB3FORMS_ACCESS_KEY';
const BMAC_PLACEHOLDER = 'https://www.buymeacoffee.com/YOUR_USERNAME';

export const WEB3FORMS_ACCESS_KEY: string =
  (import.meta.env.VITE_WEB3FORMS_KEY as string | undefined) ??
  WEB3FORMS_PLACEHOLDER;

/**
 * Ensure a URL is absolute. If the env var is set to a bare host
 * (`buymeacoffee.com/moeyjac`) with no scheme, the browser treats
 * the href as RELATIVE and resolves it against the current page —
 * producing `https://bjjmap.dev/buymeacoffee.com/moeyjac`. Prefix
 * `https://` when a scheme is missing so the link always leaves the
 * site.
 */
function ensureAbsoluteUrl(u: string): string {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u.replace(/^\/+/, '')}`;
}

export const BMAC_URL: string = ensureAbsoluteUrl(
  (import.meta.env.VITE_BMAC_URL as string | undefined) ?? BMAC_PLACEHOLDER,
);

/**
 * hCaptcha site key for spam protection on the feedback form.
 *
 * Setup (all on the user's side — the site key is public-safe, the
 * SECRET stays in Web3Forms):
 *   1. Create a free account at https://www.hcaptcha.com and add a
 *      site (any domain — hCaptcha allows localhost + your prod
 *      domain). Grab the Site Key and Secret Key.
 *   2. Paste the SECRET Key into the Web3Forms dashboard
 *      (Settings → hCaptcha). Web3Forms verifies the token server-
 *      side using that secret.
 *   3. Set VITE_HCAPTCHA_SITE_KEY (buildtime) in Coolify to the
 *      SITE key, and redeploy.
 *
 * When unset, the form still works and submits WITHOUT a captcha
 * (the widget just doesn't render). So enabling hCaptcha is a
 * config-only change — no code edit needed once this is wired.
 */
export const HCAPTCHA_SITE_KEY: string =
  (import.meta.env.VITE_HCAPTCHA_SITE_KEY as string | undefined) ?? '';

/** True once an hCaptcha site key is set — gates the widget + the
 *  "must solve captcha" submit requirement. */
export const hcaptchaEnabled = HCAPTCHA_SITE_KEY.length > 0;

/** True once a real Web3Forms key is wired in — gates the feedback
 *  UI so it doesn't render a form that would POST to a dead key. */
export const feedbackConfigured =
  WEB3FORMS_ACCESS_KEY !== WEB3FORMS_PLACEHOLDER &&
  WEB3FORMS_ACCESS_KEY.length > 0;

/** True once a real Buy Me a Coffee URL is wired in — gates the
 *  donate button. */
export const donateConfigured =
  BMAC_URL !== BMAC_PLACEHOLDER && !BMAC_URL.includes('YOUR_USERNAME');

/** Web3Forms submit endpoint. */
export const WEB3FORMS_ENDPOINT = 'https://api.web3forms.com/submit';
